import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import Button from 'design-system/src/components/Button/Button';
import { FormLabel } from '../FormLabel';
import { Input } from '../Input';
import { DropdownWithPlaceholder } from '../Dropdown.tsx';
import { getRoleOptions, getAffiliationOptions, getRequiredErrorMessage, createBeforeUnloadHandler } from './utils.tsx';
import { ControllerWrapper } from './ControllerWrapper.tsx';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { Tooltip, defaultTooltipIcon } from 'design-system/src/components/Tooltip/Tooltip';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage';
import { SignupErrorMessage } from './SignupErrorMessage';
import { ProfileInfoFormData } from './types';
import { Affiliation, AllFormData } from '../../types';
import { StyledSignupForm, NameRow } from './styles';
import ProgressCard from 'design-system/src/components/ProgressCard/ProgressCard.tsx';
import { client } from '../../api';
import { handleServerError, formatPhoneNumberIncrementally } from '../../utils';
import { PHONE_VALIDATION_REGEX } from '../../constants';
import { PageContainerWithLogo } from './PageContainerWithLogo.tsx';
import { createTrackingProps } from 'shared-utils/analytics';
import { submitHubspotForm } from '../../utils/conversionTracking';
import * as Sentry from '@sentry/react';

const NameField = styled.div`
  flex: 1;
`;

const ProfileInfoForm = ({
  handleSubmit,
  handleFormSubmit,
  control,
  errors,
  isLoading,
  serverError,
  isDemoFlow,
}: {
  handleSubmit: any;
  handleFormSubmit: any;
  control: any;
  errors: any;
  isLoading: boolean;
  serverError: string | null;
  isDemoFlow?: boolean;
}) => {
  const affiliationValue = useWatch({
    control,
    name: 'affiliation',
  });

  // Determine field visibility and labels based on affiliation
  const isPatient = affiliationValue === 'patient';
  const isNonHealthcareOrganization = ['pharmaceutical_company', 'insurance_company', 'pharmacy'].includes(
    affiliationValue,
  );

  const practiceNameLabel = isNonHealthcareOrganization ? 'Organization Name' : 'Practice Name';
  const practiceNamePlaceholder = 'e.g. New York Specialty Partners';

  const shouldShowPracticeName = !isPatient;
  const shouldShowRole = !isPatient && !isNonHealthcareOrganization;
  return (
    <StyledSignupForm onSubmit={handleSubmit(handleFormSubmit)}>
      <Typography styledAs="h5" weight={500} marginBottom="8px">
        A few details about you
      </Typography>

      <NameRow>
        <NameField>
          <ControllerWrapper>
            <Controller
              control={control}
              name="first_name"
              rules={{ required: { value: true, message: getRequiredErrorMessage('First name') } }}
              render={({ field }) => (
                <Input
                  label="First Name"
                  hasError={!!errors.first_name}
                  placeholder="John"
                  {...field}
                  {...createTrackingProps({ unmask: true })}
                />
              )}
            />
            {errors.first_name && <ErrorMessage>{errors.first_name.message}</ErrorMessage>}
          </ControllerWrapper>
        </NameField>

        <NameField>
          <ControllerWrapper>
            <Controller
              control={control}
              name="last_name"
              rules={{ required: { value: true, message: getRequiredErrorMessage('Last name') } }}
              render={({ field }) => (
                <Input
                  label="Last Name"
                  hasError={!!errors.last_name}
                  placeholder="Doe"
                  {...field}
                  {...createTrackingProps({ unmask: true })}
                />
              )}
            />
            {errors.last_name && <ErrorMessage>{errors.last_name.message}</ErrorMessage>}
          </ControllerWrapper>
        </NameField>
      </NameRow>

      <ControllerWrapper>
        <Controller
          control={control}
          name="contact_phone"
          rules={{
            pattern: {
              value: PHONE_VALIDATION_REGEX,
              message: 'Invalid phone number format',
            },
          }}
          render={({ field }) => (
            <Input
              label={
                <div className="flex w-full items-center justify-between gap-2">
                  <span className="font-normal">
                    <span className="font-semibold">Contact Number</span> Optional
                  </span>
                  <span className="ml-auto flex items-center">
                    <Tooltip content="We'll only use this for setting up your account, if needed." childNotInteractive>
                      {defaultTooltipIcon}
                    </Tooltip>
                  </span>
                </div>
              }
              placeholder="(555) 123-4567"
              type="tel"
              maxLength={14}
              hasError={!!errors.contact_phone}
              {...field}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const formatted = formatPhoneNumberIncrementally(e.target.value);
                field.onChange(formatted);
              }}
              {...createTrackingProps({ unmask: true })}
            />
          )}
        />
        {errors.contact_phone && <ErrorMessage>{errors.contact_phone.message}</ErrorMessage>}
      </ControllerWrapper>

      {shouldShowPracticeName && (
        <ControllerWrapper>
          <Controller
            control={control}
            name="practice_name"
            rules={{
              required: { value: true, message: getRequiredErrorMessage(practiceNameLabel) },
              maxLength: { value: 100, message: `${practiceNameLabel} must be 100 characters or less` },
            }}
            render={({ field }) => (
              <Input
                label={practiceNameLabel}
                hasError={!!errors.practice_name}
                placeholder={practiceNamePlaceholder}
                maxLength={100}
                {...field}
                {...createTrackingProps({ unmask: true })}
              />
            )}
          />
          {errors.practice_name && <ErrorMessage>{errors.practice_name.message}</ErrorMessage>}
        </ControllerWrapper>
      )}

      {isDemoFlow ? (
        <Controller
          name="affiliation"
          control={control}
          rules={{ required: { value: true, message: getRequiredErrorMessage('Affiliation') } }}
          render={({ field }) => (
            <ControllerWrapper>
              <FormLabel htmlFor="affiliation">Your Affiliation</FormLabel>
              <DropdownWithPlaceholder
                id="affiliation"
                $hasError={!!errors.affiliation}
                {...field}
                {...createTrackingProps({ unmask: true })}
              >
                {getAffiliationOptions()}
              </DropdownWithPlaceholder>
              {errors.affiliation && <ErrorMessage>{errors.affiliation.message}</ErrorMessage>}
            </ControllerWrapper>
          )}
        />
      ) : (
        shouldShowRole && (
          <Controller
            name="role"
            control={control}
            rules={{ required: { value: true, message: getRequiredErrorMessage('Role') } }}
            render={({ field }) => (
              <ControllerWrapper>
                <FormLabel htmlFor="role">Your Role</FormLabel>
                <DropdownWithPlaceholder
                  id="role"
                  $hasError={!!errors.role}
                  {...field}
                  {...createTrackingProps({ unmask: true })}
                >
                  {getRoleOptions()}
                </DropdownWithPlaceholder>
                {errors.role && <ErrorMessage>{errors.role.message}</ErrorMessage>}
              </ControllerWrapper>
            )}
          />
        )
      )}

      {serverError && <SignupErrorMessage error={serverError} marginBottom="16px" />}

      <Button size="lg" width="full" type="submit" isLoading={isLoading}>
        Next
      </Button>
    </StyledSignupForm>
  );
};

export type ProfileInfoProps = {
  className?: string;
  onNext: (affiliation?: Affiliation) => void;
  signupId: string;
  isDemoFlow?: boolean;
  onBeforeUnload?: (formData: ProfileInfoFormData) => void;
  defaultValues?: Partial<ProfileInfoFormData>;
  onFormDataUpdate?: (updates: Partial<ProfileInfoFormData>) => void;
  hubspotBaseData?: Partial<AllFormData>;
};

export const ProfileInfo = ({
  className,
  onNext,
  signupId,
  isDemoFlow,
  onBeforeUnload,
  defaultValues,
  onFormDataUpdate,
  hubspotBaseData,
}: ProfileInfoProps) => {
  const [error, setError] = useState<string | null>(null);

  const {
    handleSubmit,
    control,
    formState: { errors },
    getValues,
  } = useForm<ProfileInfoFormData>({
    defaultValues: defaultValues || {},
  });

  const handleBeforeUnload = useMemo(
    () => createBeforeUnloadHandler(onBeforeUnload, getValues),
    [onBeforeUnload, getValues],
  );

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [handleBeforeUnload]);

  const submitProfileInfo = useMutation({
    mutationFn: async (newFormData: ProfileInfoFormData) => {
      const requestData: any = {
        signup_id: signupId,
        first_name: newFormData.first_name,
        last_name: newFormData.last_name,
      };

      if (newFormData.contact_phone) {
        requestData.contact_phone = newFormData.contact_phone;
      }

      // Only include practice_name for non-patients
      if (newFormData.affiliation !== 'patient') {
        requestData.practice_name = newFormData.practice_name;
      }

      if (isDemoFlow) {
        requestData.affiliation = newFormData.affiliation;
      } else {
        requestData.role = newFormData.role;
      }

      const response = await client.post('/signup/submit-profile-info', requestData);

      return response.data;
    },
    onSuccess: async (_, variables) => {
      setError(null);
      onFormDataUpdate?.(variables);

      try {
        // Submit to HubSpot with available data so far (in case user drops off before completing practice info)
        // Use 'demo' form type for demo flow, 'signup' for signup flow
        await submitHubspotForm(isDemoFlow ? 'demo' : 'signup', {
          ...hubspotBaseData,
          ...variables,
        });
      } catch (err) {
        Sentry.captureException(err);
      } finally {
        onNext(variables.affiliation);
      }
    },
    onError: (error: any) => {
      console.error('Error submitting profile info:', error);
      const serverErrorMessage = handleServerError(error);
      setError(serverErrorMessage);
    },
  });

  const handleFormSubmit = async (newFormData: ProfileInfoFormData) => {
    const contact_phone = newFormData.contact_phone?.trim();
    submitProfileInfo.mutate({
      ...newFormData,
      contact_phone: contact_phone || undefined,
    });
  };

  return (
    <PageContainerWithLogo className={className} data-fs-unmask="true">
      <ProgressCard progressBar={{ current: 1, total: 2 }}>
        <ProfileInfoForm
          handleSubmit={handleSubmit}
          handleFormSubmit={handleFormSubmit}
          control={control}
          errors={errors}
          isLoading={submitProfileInfo.isPending}
          isDemoFlow={isDemoFlow}
          serverError={error}
        />
      </ProgressCard>
    </PageContainerWithLogo>
  );
};

export default ProfileInfo;
